import { InlineValue, Link, useContent, usePageContent } from "@appiodev/xcore-client/xcore-ui";
import { reverseRelation } from "@appiodev/xcore-core";
import { Box, Container, Grid, Typography } from "@xcorejs/ui";
import ImagesCarousel from "components/ImagesCarousel";
import Layout from "components/Layout";
import RobeHead from "components/RobeHead";
import VideoCarousel from "components/VideoCarousel";
import ProductCard from "design-system/robe/cards/Product";
import { useMemo } from "react";
import { RobePage, getRobePaths, getRobeProps, useLayout } from "xcore";
import { Innovation, Innovations, Product } from "xcore/types";
import useSWR from "swr";
import { GetProductGridResult } from "pages/api/robe/product-grid";
import RobeRichtext from "components/RobeRichtext";
import { PageIframe } from "design-system/robe/PageIframe";

export const getStaticProps = getRobeProps<Innovation>(({ cms, id }) => ({
  pageContent: cms.content.single(id!, {
    fetchRichtextLinks: true,
    relations: v => [
      v.video
    ],
    reverseRelations: {
      relatedProducts: reverseRelation<Product>("product", v => [v.innovations], { includeCategories: true })
    }
  }),

  content: [cms.content.single<Innovations>("innovations")]
}));

export const getStaticPaths = getRobePaths("innovation");

const InnovationDetail: RobePage = () => {
  const [{ id, values, fetchedReverseRelations }, { stringify, file, value }] = usePageContent<Innovation>();
  const [innovations] = useContent<[Innovations]>();
  const { cms: { locales: { content } }, localize } = useLayout();

  const { data: sortBy } = useSWR<GetProductGridResult>(`/api/robe/product-grid?${content.map(l => `locale=${l}`).join("&")}`);
  const typeList = sortBy?.productTypes.map(pt => pt.id);
  const sizeList = sortBy?.productSizes.map(ps => ps.id);

  // orders products by productType and productSize according to product menu
  const relatedProducts = useMemo(
    () =>
      ((fetchedReverseRelations?.relatedProducts as Product[]) ?? []).map((frr, i) =>
        ({
          ...frr,
          order: value(frr.values.discontinuedGlob) || localize(value(frr.values.discontinuedReg))
            ? 999 // discontinued products are given order on the very end
            : !frr.categories?.["product-type"]?.length || !frr.categories["product-size"]?.length
              ? 800 // products without categories or size are given order before discontinued products
              // Set order based on product type primarly and by product size secondly. If ID is not provided, product is sorted to the very end
              : getOrder(+`${typeList?.indexOf(frr.categories?.["product-type"]?.find(pt => typeList.find(tl => tl === pt.id))?.id ?? 0) ?? 999}${sizeList?.indexOf(frr.categories?.["product-size"]?.[0]?.id ?? 0) ?? 999}`)
        })
      ).sort((a, b) => a.order - b.order),
    [fetchedReverseRelations?.relatedProducts, localize, sizeList, typeList, value]
  );

  if (stringify(values.url)) {
    return (
      <Container width="100%" px="0">
        <PageIframe src={stringify(values.url)} />
      </Container>
    );
  }

  return (
    <>
      <RobeHead
        title={[stringify(innovations.title), stringify(values.name)]}
        description={stringify(values.intro)}
        img={value(values.image)}
      />

      <Container
        flexDirection="column"
        paddingTop={{ _: "3rem", sm: "5rem" }}
        paddingBottom={{ _: "6rem", sm: "10rem" }}
      >
        <Typography variant="h1" mb={{ _: "2rem", sm: "3rem" }} textAlign="center">
          <InlineValue value={stringify(values.name)} />
        </Typography>

        <div dangerouslySetInnerHTML={{ __html: stringify(values.html)! }}></div>

        <Box mb="6rem">
          <RobeRichtext
            value={values.description}
            _paragraph={{
              variant: "lead",
              textAlign: "center",
              minHeight: { _: "2.7rem", sm: "3.6rem" }
            }}
            _link={{
              fontSize: "inherit",
              lineHeight: "inherit"
            }}
          />
        </Box>

        {value(values.video) && (
          <Box width={value(values.video)!.length === 1 ? "74rem" : "100%"} maxWidth="100%" margin="auto" mb={{ _: "4rem", sm: "8rem" }}>
            <VideoCarousel
              videos={value(values.video)!}
              loop={value(values.loop)}
              autoplay={value(values.autoplay)}
              openInModal={value(values.openInModal)}
            />
          </Box>
        )}

        {value(values.images) && (
          <Box mb={{ _: "2rem", sm: "8rem" }}>
            <Box>
              <ImagesCarousel
                fitToPageContent
                disableScroll
                hideSubHeader
                images={value(values.images)!.map(img => [
                  file(img, {
                    ...value(values.images)!.length > 1 ? { width: 740, height: 416 } : { width: 1290, height: 726 },
                    format: "jpeg"
                  }),
                  value(img.name),
                  file(img, {
                    ...value(values.images)!.length > 1
                      ? { width: 740, height: 416 }
                      : { width: 1290, height: 726 },
                    enlargement: 2,
                    format: "jpeg"
                  }),
                  img.id
                ])}
              />
            </Box>
          </Box>
        )}

        {relatedProducts && (
          <Box>
            <Typography variant="h3" as="h2" textAlign="center" mb={{ _: "2rem", sm: "3rem" }}>
              <InlineValue value={innovations.values.featuredOn} />
            </Typography>

            <Grid
              columns={{ _: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }}
              rows="auto"
              gap={{ _: "2rem", sm: "3rem" }}
            >
              {relatedProducts.map(p => (
                <Link key={value(p.id)} query={`?backto=${id}`} content={p} variant="simple" overflow="hidden">
                  <ProductCard product={p} />
                </Link>
              ))}
            </Grid>
          </Box>
        )}
      </Container>
    </>
  );
};

InnovationDetail.Layout = Layout;

export default InnovationDetail;

const getOrder = (number) => number < 0 ? 999 : number;
